// @systeme: mesure-impact
// ============================================================================
// PairsForm - Page publique de réponse à un questionnaire d'impact
// ============================================================================
// Route : /impact/repondre/:token (pas d'authentification)
// Le token identifie une session individuelle (participant × questionnaire × temps)
// 1 question par page, progression enregistrée, pas de retour arrière
// Réponses anonymes via codeAnonyme fourni par le serveur
// ============================================================================

import { useState, useEffect, useCallback } from "react";
import { API_URL, messageErreur } from "../lib/api";
import { useParams } from "react-router-dom";
import {
  Box,
  Typography,
  Paper,
  Button,
  LinearProgress,
  CircularProgress,
  Alert,
  TextField,
  FormControlLabel,
  Checkbox,
} from "@mui/material";
import { ChevronRight, CheckCircle, AlertTriangle, Shield } from "lucide-react";


// ============================================================================
// Types
// ============================================================================

type QuestionType = "LIKERT" | "OUVERT";

interface Question {
  id: string;
  numero: number;
  texte: string;
  texteAide: string | null;
  estInverse: boolean;
  ordre: number;
  typeQuestion?: QuestionType;
}

interface AnswerLikert {
  type: "LIKERT";
  valeur: number;
}

interface AnswerOuvert {
  type: "OUVERT";
  texte: string;
  republication: boolean;
}

type Answer = AnswerLikert | AnswerOuvert;

interface QuestionnaireInfo {
  id: string;
  nom: string;
  nomCourt: string;
  description: string | null;
  nombreItems: number;
  dureeMinutes: number;
  echelleType: string;
  echelleMin: number;
  echelleMax: number;
  echelleLabels: Record<string, string> | null;
}

interface SessionInfo {
  id: string;
  token: string;
  tempsMesure: string;
  codeAnonyme: string;
  campagneId: string | null;
  progression: number;
}

// ============================================================================
// Helpers
// ============================================================================

const TEMPORALITE_LABELS: Record<string, string> = {
  T0: "Avant le programme",
  T1: "Mi-parcours",
  T2: "Fin du programme",
  T3: "Quelques mois après",
};

function getSavedProgress(token: string): Record<string, Answer> {
  try {
    const raw = localStorage.getItem(`impact_progress_${token}`);
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    // Backward-compat : ancien format Record<string, number>
    const out: Record<string, Answer> = {};
    for (const [qid, v] of Object.entries(parsed)) {
      if (typeof v === "number") {
        out[qid] = { type: "LIKERT", valeur: v };
      } else if (v && typeof v === "object" && "type" in (v as Record<string, unknown>)) {
        out[qid] = v as Answer;
      }
    }
    return out;
  } catch {
    return {};
  }
}

function saveProgress(token: string, answers: Record<string, Answer>) {
  localStorage.setItem(`impact_progress_${token}`, JSON.stringify(answers));
}

function isAnswered(question: Question, answer: Answer | undefined): boolean {
  if (!answer) return false;
  if (answer.type === "LIKERT") return typeof answer.valeur === "number";
  if (answer.type === "OUVERT") return answer.texte.trim().length > 0;
  return false;
}


// ============================================================================
// Composant
// ============================================================================

export default function ImpactReponsePage() {
  const { token } = useParams<{ token: string }>();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [session, setSession] = useState<SessionInfo | null>(null);
  const [questionnaire, setQuestionnaire] = useState<QuestionnaireInfo | null>(null);
  const [questions, setQuestions] = useState<Question[]>([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [answers, setAnswers] = useState<Record<string, Answer>>({});
  const [submitting, setSubmitting] = useState(false);
  const [completed, setCompleted] = useState(false);
  const [started, setStarted] = useState(false);

  // Load questionnaire data via session token
  useEffect(() => {
    if (!token) return;
    async function load() {
      try {
        const res = await fetch(`${API_URL}/impact/sessions/${token}/questionnaire`);
        if (!res.ok) {
          const data = await res.json().catch(() => null);
          if (data?.statut === "COMPLETE") {
            setCompleted(true);
            setLoading(false);
            return;
          }
          setError(data?.error || "Questionnaire non disponible");
          setLoading(false);
          return;
        }
        const data = await res.json();
        setSession(data.session);
        setQuestionnaire(data.questionnaire);
        setQuestions(data.questions || []);

        // Restore saved progress
        const saved = getSavedProgress(token!);
        if (Object.keys(saved).length > 0) {
          setAnswers(saved);
          const qs: Question[] = data.questions || [];
          let lastAnsweredIdx = -1;
          for (let i = qs.length - 1; i >= 0; i--) {
            if (qs[i].id in saved) { lastAnsweredIdx = i; break; }
          }
          if (lastAnsweredIdx >= 0 && lastAnsweredIdx < qs.length - 1) {
            setCurrentIndex(lastAnsweredIdx + 1);
            setStarted(true);
          } else if (lastAnsweredIdx === qs.length - 1) {
            setCurrentIndex(qs.length - 1);
            setStarted(true);
          }
        }
      } catch {
        setError("Impossible de charger le questionnaire");
      }
      setLoading(false);
    }
    load();
  }, [token]);

  const pushProgression = useCallback(
    (newAnswers: Record<string, Answer>) => {
      if (!token || questions.length === 0) return;
      const answeredCount = questions.reduce(
        (acc, q) => (isAnswered(q, newAnswers[q.id]) ? acc + 1 : acc),
        0
      );
      const progression = Math.round((answeredCount / questions.length) * 100);
      fetch(`${API_URL}/impact/sessions/${token}/progression`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ progression }),
      }).catch(() => {});
    },
    [token, questions]
  );

  const handleAnswer = useCallback(
    (questionId: string, value: number) => {
      if (!token) return;
      const newAnswers: Record<string, Answer> = {
        ...answers,
        [questionId]: { type: "LIKERT", valeur: value },
      };
      setAnswers(newAnswers);
      saveProgress(token, newAnswers);
      pushProgression(newAnswers);
    },
    [answers, token, pushProgression]
  );

  const handleOuvertText = useCallback(
    (questionId: string, text: string) => {
      if (!token) return;
      const prev = answers[questionId];
      const republication =
        prev && prev.type === "OUVERT" ? prev.republication : false;
      const newAnswers: Record<string, Answer> = {
        ...answers,
        [questionId]: { type: "OUVERT", texte: text, republication },
      };
      setAnswers(newAnswers);
      saveProgress(token, newAnswers);
      pushProgression(newAnswers);
    },
    [answers, token, pushProgression]
  );

  const handleOuvertRepublication = useCallback(
    (questionId: string, value: boolean) => {
      if (!token) return;
      const prev = answers[questionId];
      const texte = prev && prev.type === "OUVERT" ? prev.texte : "";
      const newAnswers: Record<string, Answer> = {
        ...answers,
        [questionId]: { type: "OUVERT", texte, republication: value },
      };
      setAnswers(newAnswers);
      saveProgress(token, newAnswers);
    },
    [answers, token]
  );

  const handleNext = useCallback(() => {
    if (currentIndex < questions.length - 1) {
      setCurrentIndex(currentIndex + 1);
    }
  }, [currentIndex, questions.length]);

  const handleSubmit = useCallback(async () => {
    if (!token || !questionnaire) return;
    setSubmitting(true);
    try {
      const reponses: Array<
        | { questionId: string; valeurReponse: number }
        | { questionId: string; texteReponse: string; republicationAutorisee: boolean }
      > = [];
      for (const questionId of Object.keys(answers)) {
        const ans = answers[questionId];
        if (ans.type === "LIKERT") {
          reponses.push({ questionId, valeurReponse: ans.valeur });
        } else if (ans.type === "OUVERT") {
          const texte = ans.texte.trim();
          if (texte.length === 0) continue; // Empty open items dropped client-side
          reponses.push({
            questionId,
            texteReponse: texte,
            republicationAutorisee: ans.republication,
          });
        }
      }
      const res = await fetch(`${API_URL}/impact/sessions/${token}/reponses`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ reponses }),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => null);
        throw new Error(data?.error || "Erreur lors de l'envoi");
      }
      setCompleted(true);
      localStorage.removeItem(`impact_progress_${token}`);
    } catch (e) {
      setError(messageErreur(e) || "Erreur lors de l'envoi des réponses");
    }
    setSubmitting(false);
  }, [token, questionnaire, answers]);

  // ============================================================================
  // Render states
  // ============================================================================

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: "100vh", bgcolor: "#F8FAFC" }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: "100vh", bgcolor: "#F8FAFC" }}>
        <Paper sx={{ p: 4, maxWidth: 500, textAlign: "center" }}>
          <AlertTriangle size={48} color="#F59E0B" />
          <Typography variant="h6" sx={{ mt: 2, fontWeight: 600 }}>
            {error}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            Si le problème persiste, contactez l'organisateur.
          </Typography>
        </Paper>
      </Box>
    );
  }

  if (completed) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: "100vh", bgcolor: "#F8FAFC" }}>
        <Paper sx={{ p: 5, maxWidth: 500, textAlign: "center" }}>
          <CheckCircle size={56} color="#10B981" />
          <Typography variant="h5" sx={{ mt: 2, fontWeight: 700, color: "#10B981" }}>
            Merci !
          </Typography>
          <Typography variant="body1" sx={{ mt: 1, color: "text.secondary" }}>
            Vos réponses ont été enregistrées de manière anonyme.
          </Typography>
          <Typography variant="body2" sx={{ mt: 2, color: "text.secondary" }}>
            Elles contribueront à mesurer l'impact du programme et à l'améliorer.
          </Typography>
          <Box sx={{ mt: 3, p: 2, bgcolor: "rgba(16,185,129,0.06)", borderRadius: 2 }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, justifyContent: "center" }}>
              <Shield size={16} color="#10B981" />
              <Typography variant="caption" sx={{ color: "#10B981", fontWeight: 600 }}>
                Vos données sont anonymisées et ne peuvent pas être reliées à votre identité.
              </Typography>
            </Box>
          </Box>
        </Paper>
      </Box>
    );
  }

  if (!questionnaire || questions.length === 0) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: "100vh", bgcolor: "#F8FAFC" }}>
        <Typography color="text.secondary">Aucune question disponible.</Typography>
      </Box>
    );
  }

  // ============================================================================
  // Intro screen
  // ============================================================================

  if (!started) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: "100vh", bgcolor: "#F8FAFC", p: 2 }}>
        <Paper sx={{ p: { xs: 3, md: 5 }, maxWidth: 600, width: "100%" }}>
          <Typography variant="h5" sx={{ fontWeight: 700, color: "#3D3D3D", mb: 1 }}>
            {questionnaire.nom}
          </Typography>
          {session && (
            <Typography variant="body2" sx={{ color: "#8B5CF6", fontWeight: 600, mb: 2 }}>
              {TEMPORALITE_LABELS[session.tempsMesure] || session.tempsMesure}
            </Typography>
          )}

          {questionnaire.description && (
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              {questionnaire.description}
            </Typography>
          )}

          <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 2, mb: 3 }}>
            <Paper variant="outlined" sx={{ p: 2, textAlign: "center" }}>
              <Typography variant="h6" sx={{ fontWeight: 700, color: "#4BB7A5" }}>
                {questionnaire.nombreItems}
              </Typography>
              <Typography variant="caption">questions</Typography>
            </Paper>
            <Paper variant="outlined" sx={{ p: 2, textAlign: "center" }}>
              <Typography variant="h6" sx={{ fontWeight: 700, color: "#4BB7A5" }}>
                ~{questionnaire.dureeMinutes} min
              </Typography>
              <Typography variant="caption">durée estimée</Typography>
            </Paper>
          </Box>

          <Alert severity="info" sx={{ mb: 3 }}>
            <Typography variant="body2">
              Pour chaque affirmation, indiquez votre degré d'accord sur une échelle de{" "}
              <strong>{questionnaire.echelleMin}</strong> à <strong>{questionnaire.echelleMax}</strong>.
              Il n'y a pas de bonne ou de mauvaise réponse.
            </Typography>
          </Alert>

          <Box sx={{ p: 2, bgcolor: "rgba(75,183,165,0.06)", borderRadius: 2, mb: 3 }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
              <Shield size={16} color="#4BB7A5" />
              <Typography variant="subtitle2" sx={{ fontWeight: 600, color: "#4BB7A5" }}>
                Anonymat garanti
              </Typography>
            </Box>
            <Typography variant="caption" color="text.secondary">
              Vos réponses sont totalement anonymes. Aucune donnée permettant de vous identifier
              n'est collectée ni stockée. Les résultats sont agrégés au niveau du groupe uniquement.
            </Typography>
          </Box>

          {Object.keys(answers).length > 0 && (
            <Alert severity="success" sx={{ mb: 2 }}>
              Vous avez déjà répondu à {Object.keys(answers).length}/{questions.length} questions.
              Vous reprendrez là où vous vous êtes arrêté(e).
            </Alert>
          )}

          <Button
            variant="contained"
            fullWidth
            size="large"
            endIcon={<ChevronRight size={18} />}
            onClick={() => setStarted(true)}
            sx={{ textTransform: "none", fontWeight: 600, py: 1.5 }}
          >
            {Object.keys(answers).length > 0 ? "Reprendre" : "Commencer"}
          </Button>
        </Paper>
      </Box>
    );
  }

  // ============================================================================
  // Question screen (1 question per page)
  // ============================================================================

  const currentQuestion = questions[currentIndex];
  const currentAnswer = answers[currentQuestion.id];
  const currentType: QuestionType = currentQuestion.typeQuestion || "LIKERT";
  const currentLikertValue =
    currentAnswer && currentAnswer.type === "LIKERT" ? currentAnswer.valeur : undefined;
  const currentOuvertText =
    currentAnswer && currentAnswer.type === "OUVERT" ? currentAnswer.texte : "";
  const currentOuvertRepublication =
    currentAnswer && currentAnswer.type === "OUVERT" ? currentAnswer.republication : false;

  // Progress : compte les questions effectivement répondues
  // (LIKERT avec une valeur, OUVERT avec du texte saisi)
  const progressedQuestions = questions.filter((q) => isAnswered(q, answers[q.id])).length;
  const progress = (progressedQuestions / questions.length) * 100;
  const isLastQuestion = currentIndex === questions.length - 1;
  // Pour valider : toutes les LIKERT doivent être répondues ; les OUVERT sont optionnelles
  const allAnswered = questions.every((q) => {
    const type = q.typeQuestion || "LIKERT";
    if (type === "OUVERT") return true;
    return isAnswered(q, answers[q.id]);
  });

  // Pour le bouton "Suivant" : on autorise toujours à avancer sur un OUVERT (optionnel)
  const canAdvance =
    currentType === "OUVERT" ? true : currentLikertValue != null;

  const scaleMin = questionnaire.echelleMin;
  const scaleMax = questionnaire.echelleMax;
  const labels = questionnaire.echelleLabels || {};
  const scaleValues: number[] = [];
  for (let i = scaleMin; i <= scaleMax; i++) {
    scaleValues.push(i);
  }

  return (
    <Box sx={{ minHeight: "100vh", bgcolor: "#F8FAFC", display: "flex", flexDirection: "column" }}>
      {/* Header */}
      <Box sx={{ bgcolor: "#fff", borderBottom: "1px solid", borderColor: "divider", px: 2, py: 1.5 }}>
        <Box sx={{ maxWidth: 600, mx: "auto", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <Typography variant="body2" sx={{ fontWeight: 600, color: "#3D3D3D" }}>
            {questionnaire.nomCourt || questionnaire.nom}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {currentIndex + 1} / {questions.length}
          </Typography>
        </Box>
        <Box sx={{ maxWidth: 600, mx: "auto", mt: 1 }}>
          <LinearProgress
            variant="determinate"
            value={progress}
            sx={{
              height: 4,
              borderRadius: 2,
              bgcolor: "rgba(0,0,0,0.06)",
              "& .MuiLinearProgress-bar": { bgcolor: "#4BB7A5", borderRadius: 2 },
            }}
          />
        </Box>
      </Box>

      {/* Question */}
      <Box sx={{ flex: 1, display: "flex", justifyContent: "center", alignItems: "center", p: 2 }}>
        <Paper sx={{ p: { xs: 3, md: 4 }, maxWidth: 600, width: "100%" }}>
          <Typography variant="caption" sx={{ color: "#8B5CF6", fontWeight: 600, mb: 1, display: "block" }}>
            Question {currentQuestion.numero}
          </Typography>

          <Typography variant="h6" sx={{ fontWeight: 600, color: "#3D3D3D", mb: 1, lineHeight: 1.4 }}>
            {currentQuestion.texte}
          </Typography>

          {currentQuestion.texteAide && (
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2, fontStyle: "italic" }}>
              {currentQuestion.texteAide}
            </Typography>
          )}

          {currentType === "OUVERT" ? (
            <Box sx={{ mt: 2 }}>
              <Alert severity="info" sx={{ mb: 2 }}>
                <Typography variant="body2">
                  Cette question est libre — vous pouvez la laisser vide si aucun changement
                  n'est attribuable à votre participation.
                </Typography>
              </Alert>

              <TextField
                multiline
                minRows={4}
                maxRows={10}
                fullWidth
                placeholder="Décrivez en quelques phrases…"
                value={currentOuvertText}
                onChange={(e) => handleOuvertText(currentQuestion.id, e.target.value)}
                inputProps={{ maxLength: 5000 }}
                sx={{ mb: 1 }}
              />
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ display: "block", textAlign: "right", mb: 2 }}
              >
                {currentOuvertText.length} / 5000
              </Typography>

              <FormControlLabel
                control={
                  <Checkbox
                    checked={currentOuvertRepublication}
                    onChange={(e) =>
                      handleOuvertRepublication(currentQuestion.id, e.target.checked)
                    }
                    sx={{
                      color: "#4BB7A5",
                      "&.Mui-checked": { color: "#4BB7A5" },
                    }}
                  />
                }
                label={
                  <Typography variant="body2" color="text.secondary">
                    J'autorise pairsform à republier anonymement ce témoignage à des fins de
                    communication. (Le texte est anonyme grâce au codeAnonyme.)
                  </Typography>
                }
                sx={{ alignItems: "flex-start", mb: 2 }}
              />
            </Box>
          ) : (
            <>
              {/* Scale labels (min and max) */}
              <Box sx={{ display: "flex", justifyContent: "space-between", mb: 1, mt: 3 }}>
                <Typography variant="caption" color="text.secondary" sx={{ maxWidth: "45%", fontSize: "0.7rem" }}>
                  {labels[String(scaleMin)] || `${scaleMin} — Pas du tout d'accord`}
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ maxWidth: "45%", textAlign: "right", fontSize: "0.7rem" }}>
                  {labels[String(scaleMax)] || `${scaleMax} — Tout à fait d'accord`}
                </Typography>
              </Box>

              {/* Scale buttons */}
              <Box sx={{ display: "flex", justifyContent: "center", gap: { xs: 0.5, sm: 1 }, mb: 3 }}>
                {scaleValues.map((val) => {
                  const isSelected = currentLikertValue === val;
                  return (
                    <Button
                      key={val}
                      variant={isSelected ? "contained" : "outlined"}
                      onClick={() => handleAnswer(currentQuestion.id, val)}
                      sx={{
                        minWidth: { xs: 44, sm: 56 },
                        height: { xs: 44, sm: 56 },
                        borderRadius: 2,
                        fontSize: { xs: "1rem", sm: "1.2rem" },
                        fontWeight: 700,
                        borderColor: isSelected ? "#4BB7A5" : "divider",
                        bgcolor: isSelected ? "#4BB7A5" : "transparent",
                        color: isSelected ? "#fff" : "#3D3D3D",
                        "&:hover": {
                          bgcolor: isSelected ? "#3DA695" : "rgba(75,183,165,0.08)",
                          borderColor: "#4BB7A5",
                        },
                        transition: "all 0.15s ease",
                      }}
                    >
                      {val}
                    </Button>
                  );
                })}
              </Box>

              {/* Middle label if exists */}
              {labels[String(Math.ceil((scaleMax + scaleMin) / 2))] && (
                <Typography variant="caption" color="text.secondary" sx={{ display: "block", textAlign: "center", mb: 2, fontSize: "0.7rem" }}>
                  {Math.ceil((scaleMax + scaleMin) / 2)} — {labels[String(Math.ceil((scaleMax + scaleMin) / 2))]}
                </Typography>
              )}
            </>
          )}

          {/* Navigation */}
          <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 2 }}>
            {isLastQuestion && allAnswered ? (
              <Button
                variant="contained"
                size="large"
                onClick={handleSubmit}
                disabled={submitting}
                startIcon={submitting ? <CircularProgress size={16} color="inherit" /> : <CheckCircle size={18} />}
                sx={{
                  textTransform: "none",
                  fontWeight: 600,
                  bgcolor: "#10B981",
                  "&:hover": { bgcolor: "#059669" },
                  px: 4,
                }}
              >
                {submitting ? "Envoi en cours..." : "Valider mes réponses"}
              </Button>
            ) : (
              <Button
                variant="contained"
                size="large"
                endIcon={<ChevronRight size={18} />}
                onClick={handleNext}
                disabled={!canAdvance}
                sx={{
                  textTransform: "none",
                  fontWeight: 600,
                  px: 4,
                }}
              >
                Suivant
              </Button>
            )}
          </Box>
        </Paper>
      </Box>
    </Box>
  );
}
